import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, UserCog, Shield, Clock, KeyRound, MapPin, Calendar, Users, Pencil, ChevronLeft, ChevronRight, Mail, Phone, User, CalendarDays, DollarSign, Scissors, Megaphone, BarChart3, Settings, MoreVertical, Target, GripVertical, Camera } from 'lucide-react';
import { format, addDays, subDays, startOfWeek } from 'date-fns';
import UpgradeGate from '../components/UpgradeGate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_ABBR = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };

const ROLE_COLORS = {
  Basic: 'bg-[#E5E7E1] text-[#5C6656]',
  Medium: 'bg-[#E9EDDF] text-[#556B2F]',
  High: 'bg-[#556B2F]/20 text-[#3E6641]',
  Full: 'bg-[#556B2F] text-white',
};

const CALENDAR_COLOURS = ['#556B2F','#7BA4DB','#5B9BD5','#6B8FB5','#8B7BB5','#B57BB5','#DB7BA4','#E57585','#D4A373','#E5B84C','#C4C44C','#6BBD6B','#4CB5A0','#7BC4D4'];

export default function StaffPage() {
  const [activeTab, setActiveTab] = useState('members');
  const [staff, setStaff] = useState([]);
  const [permRoles, setPermRoles] = useState([]);
  const [hoursDialog, setHoursDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [hours, setHours] = useState({});
  const [locations, setLocations] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [editingRole, setEditingRole] = useState(null);
  const [editPerms, setEditPerms] = useState({});
  const [permAreaTab, setPermAreaTab] = useState('calendar');
  // Full-page add/edit form
  const [fullPageMode, setFullPageMode] = useState(null); // null | 'add' | 'edit'
  const [fpSection, setFpSection] = useState('profile');
  const [fpForm, setFpForm] = useState({ name: '', last_name: '', email: '', password: '', phone: '', dob: '', role: 'staff', notes: '', job_title: '', address: '', city: '', postcode: '', country: '', calendar_colour: '#556B2F' });
  const [fpServices, setFpServices] = useState([]);
  const [fpLocations, setFpLocations] = useState([]);
  const [fpPermRole, setFpPermRole] = useState('Basic');
  const [fpStaffId, setFpStaffId] = useState(null);
  const [targetsProgress, setTargetsProgress] = useState([]);
  const [editTarget, setEditTarget] = useState(null); // { staff_id, staff_name, revenue_target, appointments_target, period }

  const fetchStaff = useCallback(async () => {
    try {
      const [sRes, pRes, lRes, svcRes] = await Promise.all([
        axios.get(`${API}/staff`, { withCredentials: true }),
        axios.get(`${API}/permissions/roles`, { withCredentials: true }),
        axios.get(`${API}/locations`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/services`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setStaff(sRes.data);
      setPermRoles(pRes.data);
      setLocations(lRes.data);
      const svcs = Array.isArray(svcRes.data) ? svcRes.data : svcRes.data?.services || [];
      setAllServices(svcs);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const fetchTargets = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/staff/targets/progress`, { withCredentials: true });
      setTargetsProgress(data);
    } catch {}
  }, []);
  useEffect(() => { if (activeTab === 'targets') fetchTargets(); }, [activeTab, fetchTargets]);

  const { t } = useTranslation();

  const openAddStaff = () => {
    setFpForm({ name: '', last_name: '', email: '', password: '', phone: '', dob: '', role: 'staff', notes: '', job_title: '', address: '', city: '', postcode: '', country: '', calendar_colour: '#556B2F' });
    setFpServices([]);
    setFpLocations([]);
    setFpPermRole('Basic');
    setFpStaffId(null);
    setFpSection('profile');
    setFullPageMode('add');
  };

  const openEditStaff = (s) => {
    setFpForm({ name: s.name || '', last_name: s.last_name || '', email: s.email || '', password: '', phone: s.phone || '', dob: s.dob || '', role: s.role || 'staff', notes: s.notes || '', job_title: s.job_title || '', address: s.address || '', city: s.city || '', postcode: s.postcode || '', country: s.country || '', calendar_colour: s.calendar_colour || '#556B2F', avatar_url: s.avatar_url || '' });
    setFpServices(s.assigned_services || []);
    setFpLocations(s.location_ids || []);
    setFpPermRole(s.permission_role || 'Basic');
    setFpStaffId(s.id);
    setFpSection('profile');
    setFullPageMode('edit');
  };

  const handleSaveStaff = async () => {
    if (!fpForm.name) { toast.error(t('staffPage.nameRequired') || 'Name is required'); return; }
    try {
      if (fullPageMode === 'add') {
        const { data: created } = await axios.post(`${API}/staff`, { name: fpForm.name, email: fpForm.email || '', password: fpForm.password || '', phone: fpForm.phone, role: fpForm.role }, { withCredentials: true });
        // Use the returned ID to update profile with services/locations/permissions
        if (created?.id) {
          await axios.put(`${API}/staff/${created.id}/profile`, {
            ...fpForm, assigned_services: fpServices, location_ids: fpLocations, permission_role: fpPermRole
          }, { withCredentials: true });
        }
        toast.success('Team member added');
      } else {
        await axios.put(`${API}/staff/${fpStaffId}/profile`, {
          ...fpForm, assigned_services: fpServices, location_ids: fpLocations, permission_role: fpPermRole
        }, { withCredentials: true });
        toast.success('Profile updated');
      }
      setFullPageMode(null);
      fetchStaff();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const toggleFpService = (svcId) => {
    setFpServices(prev => prev.includes(svcId) ? prev.filter(id => id !== svcId) : [...prev, svcId]);
  };

  const toggleFpLocation = (locId) => {
    setFpLocations(prev => prev.includes(locId) ? prev.filter(id => id !== locId) : [...prev, locId]);
  };

  const deleteStaff = async (id) => {
    if (!window.confirm('Remove this staff member?')) return;
    try {
      await axios.delete(`${API}/staff/${id}`, { withCredentials: true });
      toast.success('Staff removed');
      fetchStaff();
    } catch { toast.error('Failed'); }
  };

  // Drag-and-drop reorder (desktop + mobile touch)
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const touchDragRef = useRef({ active: false, idx: null, startY: 0, rowEls: [] });

  const handleDragStartStaff = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOverStaff = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIdx(idx);
  };

  const handleDropStaff = async (e, dropIdx) => {
    e.preventDefault();
    await reorderStaff(dragIdx, dropIdx);
  };

  const reorderStaff = async (fromIdx, toIdx) => {
    if (fromIdx === null || fromIdx === toIdx) { setDragIdx(null); setDragOverIdx(null); return; }
    const reordered = [...staff];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setStaff(reordered);
    setDragIdx(null);
    setDragOverIdx(null);
    try {
      await axios.put(`${API}/staff/reorder`, { order: reordered.map(s => s.id) }, { withCredentials: true });
      toast.success('Staff order saved');
    } catch { toast.error('Failed to save order'); fetchStaff(); }
  };

  // Touch-based drag for iPad/mobile — listeners on document so tracking works outside the grip cell
  const handleTouchStartStaff = (e, idx) => {
    e.preventDefault(); // prevent scroll while dragging
    const touch = e.touches[0];
    touchDragRef.current = { active: true, idx, startY: touch.clientY, currentIdx: idx };
    setDragIdx(idx);

    const onMove = (ev) => {
      if (!touchDragRef.current.active) return;
      ev.preventDefault();
      const t = ev.touches[0];
      const rows = document.querySelectorAll('[data-staff-drag-row]');
      for (let i = 0; i < rows.length; i++) {
        const rect = rows[i].getBoundingClientRect();
        if (t.clientY >= rect.top && t.clientY <= rect.bottom) {
          if (i !== touchDragRef.current.currentIdx) {
            touchDragRef.current.currentIdx = i;
            setDragOverIdx(i);
          }
          break;
        }
      }
    };

    const onEnd = () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      if (!touchDragRef.current.active) return;
      const from = touchDragRef.current.idx;
      const to = touchDragRef.current.currentIdx;
      touchDragRef.current.active = false;
      reorderStaff(from, to);
    };

    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
  };

  const openHours = (s) => {
    setSelectedStaff(s);
    setHours(s.working_hours || {});
    setHoursDialog(true);
  };

  const saveHours = async () => {
    try {
      await axios.put(`${API}/staff/${selectedStaff.id}/hours`, { working_hours: hours }, { withCredentials: true });
      toast.success('Hours updated');
      setHoursDialog(false);
      fetchStaff();
    } catch { toast.error('Failed'); }
  };

  const setPermRole = async (staffId, roleName) => {
    try {
      await axios.put(`${API}/staff/${staffId}/permission`, { role_name: roleName }, { withCredentials: true });
      toast.success(`Permission: ${roleName}`);
      fetchStaff();
    } catch { toast.error('Failed'); }
  };

  // ===== FULL-PAGE ADD/EDIT STAFF FORM =====
  if (fullPageMode) {
    const NAV_ITEMS = [
      { group: t('staffPage.title'), items: [{ key: 'profile', label: t('staffPage.name') }, { key: 'address', label: t('locations.address') }] },
      { group: t('staffPage.assignedServices'), items: [{ key: 'services', label: t('services.title'), count: fpServices.length || null }, { key: 'locations', label: t('locations.title') }, { key: 'settings', label: t('settings.title') }] },
    ];
    return (
      <div className="fade-in" data-testid="staff-full-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{fullPageMode === 'add' ? t('staffPage.addMember') : t('staffPage.edit')}</h1>
          <div className="flex gap-2">
            <Button variant="outline" className="border-[#E5E7E1]" onClick={() => setFullPageMode(null)} data-testid="fp-close-btn">{t('common.cancel')}</Button>
            <Button className="bg-[#1A2421] hover:bg-[#2D3B36] text-white" onClick={handleSaveStaff} data-testid="fp-save-btn">{fullPageMode === 'add' ? t('staffPage.addMember') : t('common.save')}</Button>
          </div>
        </div>
        <div className="flex gap-8">
          <div className="w-48 flex-shrink-0 space-y-4">
            {NAV_ITEMS.map(g => (
              <div key={g.group}>
                <p className="text-xs font-bold uppercase tracking-wider text-[#5C6656] px-3 mb-1">{g.group}</p>
                {g.items.map(item => (
                  <button key={item.key} onClick={() => setFpSection(item.key)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-between ${fpSection === item.key ? 'bg-[#E9EDDF] text-[#556B2F]' : 'text-[#5C6656] hover:bg-[#F5F7F0]'}`}
                    data-testid={`fp-nav-${item.key}`}>
                    <span>{item.label}</span>
                    {item.count && <span className="text-[11px] text-[#5C6656]">{item.count}</span>}
                  </button>
                ))}
              </div>
            ))}
          </div>
          <div className="flex-1 max-w-2xl space-y-6">
            {fpSection === 'profile' && (<div className="space-y-6">
              <div><h2 className="text-lg font-bold text-[#1A2421]">Profile</h2><p className="text-sm text-[#5C6656]">Manage your team member's personal profile</p></div>
              <div className="flex items-center gap-4 mb-4">
                <div className="relative w-20 h-20">
                  <div className="w-20 h-20 rounded-full overflow-hidden" style={{ backgroundColor: fpForm.calendar_colour }}>
                    {fpForm.avatar_url ? (
                      <img src={fpForm.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-white">{fpForm.name?.charAt(0)?.toUpperCase() || '?'}</div>
                    )}
                  </div>
                  <div className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-white border-2 border-[#E5E7E1] flex items-center justify-center shadow-sm pointer-events-none">
                    <Camera size={14} className="text-[#556B2F]" />
                  </div>
                  <input type="file" accept="image/*,.heic,.heif" data-testid="avatar-upload"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    style={{ fontSize: '0', zIndex: 10 }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file || !selectedStaff) return;
                      e.target.value = '';
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const { data } = await axios.post(`${API}/staff/${selectedStaff.id}/avatar`, formData, { withCredentials: true });
                        setFpForm(p => ({ ...p, avatar_url: data.avatar_url + '?t=' + Date.now() }));
                        toast.success('Photo updated');
                        fetchStaff();
                      } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
                    }} />
                </div>
                <div className="text-xs text-[#5C6656]">
                  <p className="font-medium text-[#1A2421]">Profile photo</p>
                  <p>Tap the photo to upload</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1"><Label className="text-sm font-medium">First name *</Label><Input value={fpForm.name} onChange={e => setFpForm(p => ({ ...p, name: e.target.value }))} className="border-[#E5E7E1]" data-testid="fp-name" /></div>
                <div className="space-y-1"><Label className="text-sm font-medium">Last name</Label><Input value={fpForm.last_name} onChange={e => setFpForm(p => ({ ...p, last_name: e.target.value }))} className="border-[#E5E7E1]" data-testid="fp-lastname" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1"><Label className="text-sm font-medium">Email</Label><Input type="email" value={fpForm.email} onChange={e => setFpForm(p => ({ ...p, email: e.target.value }))} className="border-[#E5E7E1]" data-testid="fp-email" placeholder="Optional — for staff login" /></div>
                <div className="space-y-1"><Label className="text-sm font-medium">Phone number</Label><Input value={fpForm.phone} onChange={e => setFpForm(p => ({ ...p, phone: e.target.value }))} className="border-[#E5E7E1]" data-testid="fp-phone" /></div>
              </div>
              {fullPageMode === 'add' && (<div className="space-y-1"><Label className="text-sm font-medium">Password</Label><Input type="password" value={fpForm.password} onChange={e => setFpForm(p => ({ ...p, password: e.target.value }))} className="border-[#E5E7E1]" data-testid="fp-password" placeholder="Optional — for staff login" /></div>)}
              {fullPageMode === 'edit' && (<div className="space-y-1"><Label className="text-sm font-medium">Set / Change Password</Label><Input type="password" value={fpForm.password} onChange={e => setFpForm(p => ({ ...p, password: e.target.value }))} className="border-[#E5E7E1]" data-testid="fp-password" placeholder="Leave empty to keep current" /></div>)}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1"><Label className="text-sm font-medium">Birthday</Label><Input type="date" value={fpForm.dob} onChange={e => setFpForm(p => ({ ...p, dob: e.target.value }))} className="border-[#E5E7E1]" /></div>
                <div className="space-y-1"><Label className="text-sm font-medium">Role</Label><Select value={fpForm.role} onValueChange={v => setFpForm(p => ({ ...p, role: v }))}><SelectTrigger className="border-[#E5E7E1]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="staff">Staff</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent></Select></div>
              </div>
              <div className="space-y-2"><Label className="text-sm font-medium">Calendar colour</Label>
                <div className="flex flex-wrap gap-2">{CALENDAR_COLOURS.map(c => (<button key={c} onClick={() => setFpForm(p => ({ ...p, calendar_colour: c }))} className={`w-8 h-8 rounded-full transition-all ${fpForm.calendar_colour === c ? 'ring-2 ring-offset-2 ring-[#1A2421] scale-110' : 'hover:scale-105'}`} style={{ backgroundColor: c }} />))}</div>
              </div>
              <div className="space-y-1"><Label className="text-sm font-medium">Job title</Label><Input value={fpForm.job_title} onChange={e => setFpForm(p => ({ ...p, job_title: e.target.value }))} placeholder="Visible to clients online" className="border-[#E5E7E1]" /></div>
              <div className="space-y-1"><Label className="text-sm font-medium">Notes</Label><Input value={fpForm.notes} onChange={e => setFpForm(p => ({ ...p, notes: e.target.value }))} placeholder="Internal notes..." className="border-[#E5E7E1]" /></div>
            </div>)}
            {fpSection === 'address' && (<div className="space-y-6">
              <div><h2 className="text-lg font-bold text-[#1A2421]">Addresses</h2><p className="text-sm text-[#5C6656]">Manage your team member's address details</p></div>
              <div className="space-y-1"><Label className="text-sm font-medium">Address</Label><Input value={fpForm.address} onChange={e => setFpForm(p => ({ ...p, address: e.target.value }))} className="border-[#E5E7E1]" /></div>
              <div className="grid grid-cols-2 gap-4"><div className="space-y-1"><Label className="text-sm font-medium">City</Label><Input value={fpForm.city} onChange={e => setFpForm(p => ({ ...p, city: e.target.value }))} className="border-[#E5E7E1]" /></div><div className="space-y-1"><Label className="text-sm font-medium">Postcode</Label><Input value={fpForm.postcode} onChange={e => setFpForm(p => ({ ...p, postcode: e.target.value }))} className="border-[#E5E7E1]" /></div></div>
              <div className="space-y-1"><Label className="text-sm font-medium">Country</Label><Input value={fpForm.country} onChange={e => setFpForm(p => ({ ...p, country: e.target.value }))} className="border-[#E5E7E1]" /></div>
            </div>)}
            {fpSection === 'services' && (<div className="space-y-6" data-testid="fp-services-section">
              <div><h2 className="text-lg font-bold text-[#1A2421]">Services</h2><p className="text-sm text-[#5C6656]">Select which services this team member can perform. Uncheck categories or individual services to restrict.</p></div>
              <div className="flex items-center justify-between mb-2"><span className="text-sm text-[#5C6656]">{fpServices.length ? `${fpServices.length} selected` : 'No services selected'}</span><div className="flex gap-2">{fpServices.length !== allServices.length && <button onClick={() => setFpServices(allServices.map(s => s.id))} className="text-xs text-[#556B2F] hover:underline" data-testid="select-all-services">Select all</button>}{fpServices.length > 0 && <button onClick={() => setFpServices([])} className="text-xs text-[#E2725B] hover:underline">Clear all</button>}</div></div>
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {(() => {
                  // Group services by category
                  const cats = {};
                  allServices.forEach(svc => {
                    const cat = svc.category || 'Uncategorised';
                    if (!cats[cat]) cats[cat] = [];
                    cats[cat].push(svc);
                  });
                  return Object.entries(cats).map(([catName, catServices]) => {
                    const allChecked = catServices.every(s => fpServices.includes(s.id));
                    const someChecked = catServices.some(s => fpServices.includes(s.id));
                    const toggleCat = () => {
                      if (allChecked) {
                        setFpServices(prev => prev.filter(id => !catServices.find(s => s.id === id)));
                      } else {
                        setFpServices(prev => [...new Set([...prev, ...catServices.map(s => s.id)])]);
                      }
                    };
                    return (
                      <div key={catName} className="border border-[#E5E7E1] rounded-xl overflow-hidden">
                        <button onClick={toggleCat} className="w-full flex items-center gap-3 px-4 py-3 bg-[#F9F8F6] hover:bg-[#F5F7F0] transition-colors text-left" data-testid={`cat-toggle-${catName}`}>
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${allChecked ? 'bg-[#556B2F] border-[#556B2F]' : someChecked ? 'bg-[#556B2F]/40 border-[#556B2F]' : 'border-[#D1D5CB] bg-white'}`}>
                            {(allChecked || someChecked) && <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                          </div>
                          <div className="flex-1"><p className="text-sm font-bold text-[#1A2421]">{catName}</p><p className="text-[10px] text-[#5C6656]">{catServices.filter(s => fpServices.includes(s.id)).length}/{catServices.length} selected</p></div>
                        </button>
                        <div className="divide-y divide-[#E5E7E1]/50">
                          {catServices.map(svc => { const checked = fpServices.includes(svc.id); return (
                            <label key={svc.id} className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${checked ? 'bg-[#F5F7F0]' : 'hover:bg-[#F9F8F6]'}`} data-testid={`fp-svc-${svc.id}`}>
                              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${checked ? 'bg-[#556B2F] border-[#556B2F]' : 'border-[#D1D5CB] bg-white'}`} onClick={(e) => { e.preventDefault(); toggleFpService(svc.id); }}>{checked && <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}</div>
                              <div className="flex-1 min-w-0"><p className="text-sm text-[#1A2421]">{svc.name}</p></div>
                              <span className="text-xs text-[#5C6656]">{svc.duration}min</span>
                              <span className="text-sm font-medium text-[#556B2F]">{'\u00A3'}{(svc.price || 0).toFixed(0)}</span>
                            </label>); })}
                        </div>
                      </div>
                    );
                  });
                })()}
                {allServices.length === 0 && <p className="text-center text-[#5C6656] py-8 text-sm">No services configured yet</p>}
              </div>
            </div>)}
            {fpSection === 'locations' && (<div className="space-y-6" data-testid="fp-locations-section">
              <div><h2 className="text-lg font-bold text-[#1A2421]">Locations</h2><p className="text-sm text-[#5C6656]">Select which locations this team member works at. If none selected, they work at all locations.</p></div>
              <div className="space-y-1">{locations.map(loc => { const checked = fpLocations.includes(loc.id); return (
                <label key={loc.id} className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-colors ${checked ? 'border-[#556B2F] bg-[#F5F7F0]' : 'border-[#E5E7E1] hover:border-[#556B2F]/30'}`} data-testid={`fp-loc-${loc.id}`}>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${checked ? 'bg-[#556B2F] border-[#556B2F]' : 'border-[#D1D5CB] bg-white'}`} onClick={(e) => { e.preventDefault(); toggleFpLocation(loc.id); }}>{checked && <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}</div>
                  <div className="flex-1"><p className="text-sm font-medium text-[#1A2421]">{loc.name}</p>{loc.address && <p className="text-[11px] text-[#5C6656]">{loc.address}</p>}</div>
                </label>); })}{locations.length === 0 && <p className="text-center text-[#5C6656] py-8 text-sm">No locations configured yet</p>}</div>
            </div>)}
            {fpSection === 'settings' && (<div className="space-y-6" data-testid="fp-settings-section">
              <div><h2 className="text-lg font-bold text-[#1A2421]">Settings</h2><p className="text-sm text-[#5C6656]">Set the permission level for this team member</p></div>
              <div className="space-y-2">{permRoles.map(r => (
                <label key={r.name} className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-colors ${fpPermRole === r.name ? 'border-[#556B2F] bg-[#F5F7F0]' : 'border-[#E5E7E1] hover:border-[#556B2F]/30'}`} onClick={() => setFpPermRole(r.name)} data-testid={`fp-perm-${r.name}`}>
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${fpPermRole === r.name ? 'border-[#556B2F]' : 'border-[#D1D5CB]'}`}>{fpPermRole === r.name && <div className="w-2 h-2 rounded-full bg-[#556B2F]" />}</div>
                  <div><p className="text-sm font-medium text-[#1A2421]">{r.name}</p><p className="text-[11px] text-[#5C6656]">{r.description}</p></div>
                </label>))}</div>
            </div>)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in space-y-6" data-testid="staff-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-medium text-[#1A2421] tracking-tight" style={{ fontFamily: 'Outfit' }}>{t('staffPage.title')}</h1>
          <p className="text-sm text-[#5C6656] mt-1">{staff.length} {t('staffPage.teamMembers')}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-[#F9F8F6] rounded-lg p-0.5 border border-[#E5E7E1]">
            <button onClick={() => setActiveTab('members')} data-testid="tab-members"
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${activeTab === 'members' ? 'bg-white text-[#556B2F] shadow-sm' : 'text-[#5C6656] hover:text-[#1A2421]'}`}>
              <Users size={14} /> Team Members
            </button>
            <button onClick={() => setActiveTab('shifts')} data-testid="tab-shifts"
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${activeTab === 'shifts' ? 'bg-white text-[#556B2F] shadow-sm' : 'text-[#5C6656] hover:text-[#1A2421]'}`}>
              <Calendar size={14} /> Scheduled Shifts
            </button>
            <button onClick={() => setActiveTab('permissions')} data-testid="tab-permissions"
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${activeTab === 'permissions' ? 'bg-white text-[#556B2F] shadow-sm' : 'text-[#5C6656] hover:text-[#1A2421]'}`}>
              <Shield size={14} /> {t('staffPage.permissions')}
            </button>
            <button onClick={() => setActiveTab('targets')} data-testid="tab-targets"
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${activeTab === 'targets' ? 'bg-white text-[#556B2F] shadow-sm' : 'text-[#5C6656] hover:text-[#1A2421]'}`}>
              <Target size={14} /> {t('staffPage.title')} KPI
            </button>
          </div>
          {activeTab === 'members' && (
            <Button data-testid="add-staff-btn" onClick={openAddStaff} className="bg-[#556B2F] hover:bg-[#4A5D23] text-white"><Plus size={16} className="mr-1" /> Add Staff</Button>
          )}
        </div>
      </div>

      {activeTab === 'members' && (
      <Card className="border-[#E5E7E1] shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow className="border-[#E5E7E1]">
              <TableHead className="text-[#5C6656] w-8"></TableHead>
              <TableHead className="text-[#5C6656]">Name</TableHead>
              <TableHead className="text-[#5C6656]">Email</TableHead>
              <TableHead className="text-[#5C6656]">Phone</TableHead>
              <TableHead className="text-[#5C6656]">Role</TableHead>
              <TableHead className="text-[#5C6656]">Permission</TableHead>
              <TableHead className="text-[#5C6656]">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {staff.map((s, idx) => (
                <TableRow key={s.id}
                  data-staff-drag-row={idx}
                  draggable
                  onDragStart={(e) => handleDragStartStaff(e, idx)}
                  onDragOver={(e) => handleDragOverStaff(e, idx)}
                  onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                  onDrop={(e) => handleDropStaff(e, idx)}
                  className={`border-[#E5E7E1] transition-all ${dragIdx === idx ? 'opacity-40' : ''} ${dragOverIdx === idx && dragIdx !== idx ? 'border-t-2 border-t-[#556B2F]' : ''}`}
                  data-testid={`staff-row-${s.id}`}>
                  <TableCell className="w-8 cursor-grab active:cursor-grabbing px-2"
                    onTouchStart={(e) => handleTouchStartStaff(e, idx)}>
                    <GripVertical size={16} className="text-[#B0B5A8] hover:text-[#556B2F]" />
                  </TableCell>
                  <TableCell>
                    <button onClick={() => openEditStaff(s)} className="flex items-center gap-2 hover:underline text-left" data-testid={`staff-name-${s.id}`}>
                      <div className="w-8 h-8 rounded-full bg-[#E9EDDF] flex items-center justify-center text-xs font-bold text-[#556B2F] overflow-hidden">
                        {s.avatar_url ? <img src={s.avatar_url} alt="" className="w-full h-full object-cover" /> : s.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <span className="font-medium text-[#556B2F]">{s.name}</span>
                    </button>
                  </TableCell>
                  <TableCell className="text-[#5C6656]">{s.email}</TableCell>
                  <TableCell className="text-[#5C6656]">{s.phone || '-'}</TableCell>
                  <TableCell><Badge className={s.role === 'admin' ? 'bg-[#556B2F] text-white' : 'bg-[#E9EDDF] text-[#556B2F]'}>{s.role}</Badge></TableCell>
                  <TableCell>
                    <Select value={s.permission_role || 'Basic'} onValueChange={v => setPermRole(s.id, v)}>
                      <SelectTrigger className="h-7 text-xs w-24 border-[#E5E7E1]"><SelectValue /></SelectTrigger>
                      <SelectContent>{permRoles.map(r => <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openHours(s)} className="h-7 w-7 p-0 text-[#556B2F]" data-testid={`hours-btn-${s.id}`}><Clock size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => openEditStaff(s)} className="h-7 w-7 p-0 text-[#556B2F]"><Pencil size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteStaff(s.id)} className="h-7 w-7 p-0 text-[#E2725B]" data-testid={`delete-staff-${s.id}`}><Trash2 size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      )}

      {activeTab === 'shifts' && (
        <ScheduledShifts staff={staff} onRefresh={fetchStaff} locations={locations} />
      )}

      {activeTab === 'permissions' && !editingRole && (
        <UpgradeGate feature="permissions" featureName="Permission Roles">
        <PermissionRolesList roles={permRoles} staff={staff} onEdit={(role) => { setEditingRole(role); setEditPerms(JSON.parse(JSON.stringify(role.permissions || {}))); setPermAreaTab('calendar'); }} onRefresh={fetchStaff} />
        </UpgradeGate>
      )}

      {activeTab === 'permissions' && editingRole && (
        <UpgradeGate feature="permissions" featureName="Permission Roles">
        <PermissionRoleEditor role={editingRole} perms={editPerms} setPerms={setEditPerms} activeArea={permAreaTab} setActiveArea={setPermAreaTab}
          onClose={() => setEditingRole(null)}
          onSave={async () => {
            try {
              await axios.put(`${API}/permissions/roles/${editingRole.id}`, { permissions: editPerms }, { withCredentials: true });
              toast.success('Permissions saved');
              setEditingRole(null);
              fetchStaff();
            } catch { toast.error('Failed to save'); }
          }} />
        </UpgradeGate>
      )}

      {/* Team Targets / KPI */}
      {activeTab === 'targets' && (
        <UpgradeGate feature="team_targets" featureName="Team Targets">
        <div className="space-y-4" data-testid="team-targets">
          <div className="flex items-center justify-between">
            <p className="text-sm text-[#5C6656]">{t('staffPage.title')} KPI — {t('common.thisMonth')}</p>
          </div>
          {/* Target cards grid */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {staff.filter(s => s.is_active).map(s => {
              const prog = targetsProgress.find(p => p.staff_id === s.id);
              const hasTarget = prog && (prog.revenue_target > 0 || prog.appointments_target > 0);
              const revPct = prog?.revenue_progress || 0;
              const apptPct = prog?.appointments_progress || 0;
              const revColor = revPct >= 80 ? '#3E6641' : revPct >= 50 ? '#D4A373' : '#E2725B';
              const apptColor = apptPct >= 80 ? '#3E6641' : apptPct >= 50 ? '#D4A373' : '#E2725B';
              return (
                <Card key={s.id} className="border-[#E5E7E1] hover:border-[#556B2F]/30 transition-colors" data-testid={`target-card-${s.id}`}>
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ background: s.calendar_colour || '#556B2F' }}>{(s.name || '?')[0]}</div>
                        <div><p className="text-sm font-semibold text-[#1A2421]">{s.name}</p><p className="text-[10px] text-[#5C6656] capitalize">{s.role}</p></div>
                      </div>
                      <button onClick={() => setEditTarget({ staff_id: s.id, staff_name: s.name, revenue_target: prog?.revenue_target || 0, appointments_target: prog?.appointments_target || 0, period: prog?.period || 'monthly' })}
                        className="text-xs text-[#556B2F] hover:underline" data-testid={`edit-target-${s.id}`}><Pencil size={12} /></button>
                    </div>
                    {hasTarget ? (
                      <div className="space-y-3">
                        {prog.revenue_target > 0 && (
                          <div>
                            <div className="flex justify-between text-xs mb-1"><span className="text-[#5C6656]">{t('common.total')}</span><span className="font-medium" style={{ color: revColor }}>{'\u00A3'}{prog.actual_revenue?.toFixed(0)} / {'\u00A3'}{prog.revenue_target}</span></div>
                            <div className="h-2 bg-[#F5F7F0] rounded-full overflow-hidden"><div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, revPct)}%`, background: revColor }} /></div>
                            <p className="text-[10px] text-right mt-0.5" style={{ color: revColor }}>{revPct.toFixed(0)}%</p>
                          </div>
                        )}
                        {prog.appointments_target > 0 && (
                          <div>
                            <div className="flex justify-between text-xs mb-1"><span className="text-[#5C6656]">{t('appointments.title')}</span><span className="font-medium" style={{ color: apptColor }}>{prog.actual_appointments} / {prog.appointments_target}</span></div>
                            <div className="h-2 bg-[#F5F7F0] rounded-full overflow-hidden"><div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, apptPct)}%`, background: apptColor }} /></div>
                            <p className="text-[10px] text-right mt-0.5" style={{ color: apptColor }}>{apptPct.toFixed(0)}%</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-[#5C6656] text-center py-3 bg-[#F9F8F6] rounded-lg">{t('common.noData')}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
          {/* Edit target dialog */}
          <Dialog open={!!editTarget} onOpenChange={() => setEditTarget(null)}>
            <DialogContent className="sm:max-w-sm border-[#E5E7E1]">
              <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}><Target size={18} className="inline mr-2 text-[#556B2F]" />{editTarget?.staff_name} — KPI</DialogTitle></DialogHeader>
              {editTarget && (
                <div className="space-y-4 mt-2">
                  <div className="space-y-1"><Label className="text-xs">{t('common.total')} ({t('common.thisMonth')})</Label><Input type="number" min="0" value={editTarget.revenue_target} onChange={e => setEditTarget(p => ({ ...p, revenue_target: parseFloat(e.target.value) || 0 }))} className="border-[#E5E7E1]" placeholder="e.g. 3000" data-testid="target-revenue-input" /></div>
                  <div className="space-y-1"><Label className="text-xs">{t('appointments.title')} ({t('common.thisMonth')})</Label><Input type="number" min="0" value={editTarget.appointments_target} onChange={e => setEditTarget(p => ({ ...p, appointments_target: parseInt(e.target.value) || 0 }))} className="border-[#E5E7E1]" placeholder="e.g. 100" data-testid="target-appts-input" /></div>
                  <Button onClick={async () => {
                    try {
                      await axios.put(`${API}/staff/${editTarget.staff_id}/target`, { revenue_target: editTarget.revenue_target, appointments_target: editTarget.appointments_target, period: 'monthly' }, { withCredentials: true });
                      toast.success(t('common.save'));
                      setEditTarget(null);
                      fetchTargets();
                    } catch { toast.error('Failed'); }
                  }} className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white" data-testid="save-target-btn">{t('common.save')}</Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
        </UpgradeGate>
      )}



      {/* Working Hours Dialog */}
      <Dialog open={hoursDialog} onOpenChange={setHoursDialog}>
        <DialogContent className="sm:max-w-lg border-[#E5E7E1]">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}><Clock size={18} className="inline mr-2 text-[#556B2F]" />Working Hours — {selectedStaff?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            {DAYS.map(day => {
              const d = hours[day] || { enabled: false, start: '09:00', end: '17:00' };
              return (
                <div key={day} className="flex items-center gap-3">
                  <Switch checked={d.enabled} onCheckedChange={v => setHours(prev => ({ ...prev, [day]: { ...d, enabled: v } }))} />
                  <span className="w-12 text-sm font-medium text-[#1A2421] capitalize">{DAY_ABBR[day]}</span>
                  {d.enabled ? (
                    <div className="flex items-center gap-2">
                      <Input type="time" value={d.start} onChange={e => setHours(prev => ({ ...prev, [day]: { ...d, start: e.target.value } }))} className="w-28 h-8 text-xs border-[#E5E7E1]" />
                      <span className="text-xs text-[#5C6656]">to</span>
                      <Input type="time" value={d.end} onChange={e => setHours(prev => ({ ...prev, [day]: { ...d, end: e.target.value } }))} className="w-28 h-8 text-xs border-[#E5E7E1]" />
                    </div>
                  ) : <span className="text-xs text-[#B0B5A8]">Not working</span>}
                </div>
              );
            })}
            <Button onClick={saveHours} className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white mt-2">Save Hours</Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}

/* ========== PERMISSION ROLES LIST ========== */
const PERM_AREA_ICONS = { calendar: CalendarDays, sales: DollarSign, clients: Users, services: Scissors, marketing: Megaphone, team: UserCog, reports: BarChart3, settings: Settings };

function PermissionRolesList({ roles, staff, onEdit, onRefresh }) {
  const { t } = useTranslation();
  const defaultRoles = roles.filter(r => r.is_default);
  const customRoles = roles.filter(r => !r.is_default);
  const getStaffCount = (roleName) => staff.filter(s => s.permission_role === roleName).length;

  const RoleCard = ({ role }) => (
    <div className="p-5 border border-[#E5E7E1] rounded-xl hover:border-[#556B2F]/30 transition-colors cursor-pointer group" onClick={() => onEdit(role)} data-testid={`perm-role-${role.name}`}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[#1A2421] group-hover:text-[#556B2F] transition-colors">{role.name}</h3>
          <p className="text-xs text-[#5C6656] mt-1 line-clamp-2">{role.description}</p>
        </div>
        {getStaffCount(role.name) > 0 && (
          <div className="flex -space-x-2">
            {staff.filter(s => s.permission_role === role.name).slice(0, 3).map(s => (
              <div key={s.id} className="w-7 h-7 rounded-full bg-[#E9EDDF] border-2 border-white flex items-center justify-center text-[10px] font-bold text-[#556B2F]">{s.name?.charAt(0)?.toUpperCase()}</div>
            ))}
            {getStaffCount(role.name) > 3 && <div className="w-7 h-7 rounded-full bg-[#F5F5F3] border-2 border-white flex items-center justify-center text-[10px] text-[#5C6656]">+{getStaffCount(role.name) - 3}</div>}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="permission-roles-list">
      <div>
        <h2 className="text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Permission roles</h2>
        <p className="text-sm text-[#5C6656] mt-1">Choose which areas are accessible to each permission role</p>
      </div>
      <div className="space-y-3">
        {defaultRoles.map(r => <RoleCard key={r.id || r.name} role={r} />)}
      </div>
      {customRoles.length > 0 && (
        <>
          <h3 className="text-sm font-bold text-[#5C6656] uppercase tracking-wider">Custom</h3>
          <div className="space-y-3">{customRoles.map(r => <RoleCard key={r.id || r.name} role={r} />)}</div>
        </>
      )}
    </div>
  );
}

/* ========== PERMISSION ROLE EDITOR (Fresha-style) ========== */
const PERM_AREA_CONFIG = {
  calendar: {
    label: 'Calendar', description: 'Allow access to the calendar and decide what actions they can perform.',
    sections: [
      { title: 'Calendar access and actions', items: [
        { key: 'view_own', label: 'Can view their own calendar', disabled: true },
        { key: 'view_others', label: "Can view other team members' calendars", indent: true },
        { key: 'create_appointments', label: 'Can create appointments' },
        { key: 'apply_discounts', label: 'Can apply discounts to appointments', indent: true },
      ]},
      { title: 'Blocked times', items: [
        { key: 'create_blocked_own', label: 'Can create a blocked time in their own calendar' },
        { key: 'create_blocked_others', label: 'Can create a blocked time for other team members' },
        { key: 'manage_blocked_types', label: 'Can set up and change blocked time types for the workspace' },
      ]},
      { title: 'Booking restrictions', items: [
        { key: 'book_unassigned_staff', label: 'Can book a service with team members not assigned to deliver that service' },
        { key: 'book_unset_resources', label: 'Can book a service with resources not set up to deliver that service' },
      ]},
      { title: 'Calendar settings', items: [
        { key: 'manage_closed_dates', label: 'Can manage business closed dates' },
      ]},
    ]
  },
  sales: {
    label: 'Sales', description: 'Allow access to sales, invoices and payment features.',
    sections: [
      { title: 'Sales access', items: [
        { key: 'view_own', label: 'Can view own sales' },
        { key: 'view_all', label: 'Can view all sales', indent: true },
        { key: 'view_gift_cards', label: 'Can view gift cards sold' },
      ]},
      { title: 'Invoice actions', items: [
        { key: 'create_invoices', label: 'Can create invoices' },
        { key: 'void_invoices', label: 'Can void invoices' },
        { key: 'apply_discounts', label: 'Can apply discounts' },
      ]},
    ]
  },
  clients: {
    label: 'Clients', description: 'Allow access to client records and manage client data.',
    sections: [
      { title: 'Client access', items: [
        { key: 'view', label: 'Can view clients' },
        { key: 'create', label: 'Can create new clients' },
        { key: 'edit', label: 'Can edit client details' },
        { key: 'delete', label: 'Can delete clients' },
        { key: 'export', label: 'Can export client data' },
      ]},
    ]
  },
  services: {
    label: 'Catalogue', description: 'Allow access to service catalogue and pricing.',
    sections: [
      { title: 'Catalogue access', items: [
        { key: 'view', label: 'Can view services and prices' },
        { key: 'manage', label: 'Can create, edit, and delete services' },
      ]},
    ]
  },
  marketing: {
    label: 'Marketing', description: 'Allow access to marketing automations and message history.',
    sections: [
      { title: 'Marketing access', items: [
        { key: 'view', label: 'Can view marketing automations and messages' },
        { key: 'manage', label: 'Can enable/disable automations and manage campaigns' },
      ]},
    ]
  },
  team: {
    label: 'Team', description: 'Allow access to team management features.',
    sections: [
      { title: 'Team access', items: [
        { key: 'view', label: 'Can view team members' },
        { key: 'manage', label: 'Can add, edit, and remove team members' },
        { key: 'manage_shifts', label: 'Can manage scheduled shifts' },
      ]},
    ]
  },
  reports: {
    label: 'Reports', description: 'Allow access to reports and analytics.',
    sections: [
      { title: 'Reports access', items: [
        { key: 'view', label: 'Can view reports and analytics' },
      ]},
    ]
  },
  settings: {
    label: 'Workspace', description: 'Allow access to business settings and configuration.',
    sections: [
      { title: 'Settings access', items: [
        { key: 'view', label: 'Can view business settings' },
        { key: 'manage', label: 'Can change business settings' },
      ]},
    ]
  },
};

function PermissionRoleEditor({ role, perms, setPerms, activeArea, setActiveArea, onClose, onSave }) {
  const areaPerms = perms[activeArea] || {};
  const config = PERM_AREA_CONFIG[activeArea];
  const isEnabled = areaPerms.enabled !== false;
  const hasAnyEnabled = (area) => {
    const ap = perms[area] || {};
    return ap.enabled !== false && Object.entries(ap).some(([k, v]) => k !== 'enabled' && v === true);
  };

  const toggleArea = (val) => {
    setPerms(p => ({ ...p, [activeArea]: { ...p[activeArea], enabled: val } }));
  };
  const togglePerm = (key) => {
    setPerms(p => ({ ...p, [activeArea]: { ...p[activeArea], [key]: !areaPerms[key] } }));
  };

  return (
    <div className="space-y-6" data-testid="permission-role-editor">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Edit {role.name} permission role</h1>
          <p className="text-sm text-[#5C6656] mt-1">Select the areas of your workspace that team members in this role can access and manage</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="border-[#E5E7E1]" data-testid="perm-close-btn">Close</Button>
          <Button onClick={onSave} className="bg-[#1A2421] hover:bg-[#2D3B36] text-white" data-testid="perm-save-btn">Save</Button>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Left sidebar - Permission areas */}
        <div className="w-56 flex-shrink-0">
          <Card className="border-[#E5E7E1]">
            <CardContent className="p-3">
              <p className="text-xs font-bold uppercase tracking-wider text-[#5C6656] px-3 py-2">Permission area</p>
              {Object.entries(PERM_AREA_CONFIG).map(([key, cfg]) => {
                const Icon = PERM_AREA_ICONS[key] || Settings;
                const active = hasAnyEnabled(key);
                const isSelected = activeArea === key;
                return (
                  <button key={key} onClick={() => setActiveArea(key)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isSelected ? 'bg-[#F5F5F3] text-[#1A2421]' : 'text-[#5C6656] hover:bg-[#F9F8F6]'}`}
                    data-testid={`perm-area-${key}`}>
                    <Icon size={16} className={isSelected ? 'text-[#556B2F]' : 'text-[#5C6656]'} />
                    <span className="flex-1 text-left">{cfg.label}</span>
                    <span className={`w-2.5 h-2.5 rounded-full ${active ? 'bg-[#556B2F]' : 'bg-[#D1D5CB]'}`} />
                  </button>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Right content - Permission checkboxes */}
        <div className="flex-1 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>
                {config.label}
                {isEnabled && <Badge className="ml-2 bg-[#3E6641] text-white text-[10px]">Active</Badge>}
                {!isEnabled && <Badge className="ml-2 bg-[#E5E7E1] text-[#5C6656] text-[10px]">Inactive</Badge>}
              </h2>
              <p className="text-sm text-[#5C6656] mt-1">{config.description}</p>
            </div>
            <Switch checked={isEnabled} onCheckedChange={toggleArea} data-testid={`perm-toggle-${activeArea}`} />
          </div>

          {config.sections.map((section, si) => (
            <div key={si}>
              <h3 className="text-sm font-bold text-[#1A2421] mb-3">{section.title}</h3>
              <div className="space-y-2">
                {section.items.map(item => {
                  const checked = isEnabled && areaPerms[item.key] !== false && areaPerms[item.key] === true;
                  return (
                    <label key={item.key} className={`flex items-center gap-3 py-1.5 cursor-pointer ${item.indent ? 'pl-8' : ''} ${!isEnabled ? 'opacity-40 pointer-events-none' : ''}`} data-testid={`perm-check-${activeArea}-${item.key}`}>
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${checked ? 'bg-[#556B2F] border-[#556B2F]' : 'border-[#D1D5CB] bg-white'} ${item.disabled ? 'opacity-50' : ''}`}
                        onClick={(e) => { if (!item.disabled) { e.preventDefault(); togglePerm(item.key); } }}>
                        {checked && <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </div>
                      <span className={`text-sm ${item.disabled ? 'text-[#B0B5A8]' : 'text-[#1A2421]'}`}>{item.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ========== SCHEDULED SHIFTS (Fresha-style interactive) ========== */
function ScheduledShifts({ staff, onRefresh, locations = [] }) {
  const { t } = useTranslation();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [editCell, setEditCell] = useState(null); // { staffId, day, date }
  const [editStart, setEditStart] = useState('09:00');
  const [editEnd, setEditEnd] = useState('17:00');
  const [cellMenu, setCellMenu] = useState(null);
  const [overrides, setOverrides] = useState([]);

  const weekDates = DAYS.map((_, i) => addDays(weekStart, i));
  const dateFrom = format(weekDates[0], 'yyyy-MM-dd');
  const dateTo = format(weekDates[6], 'yyyy-MM-dd');

  // Fetch overrides for visible week
  useEffect(() => {
    axios.get(`${API}/staff/schedule-overrides?date_from=${dateFrom}&date_to=${dateTo}`, { withCredentials: true })
      .then(r => setOverrides(r.data || []))
      .catch(() => setOverrides([]));
  }, [dateFrom, dateTo]);

  // Get effective schedule for a staff member on a specific date (override > weekly pattern)
  const getEffective = (staffId, dayName, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const ov = overrides.find(o => o.staff_id === staffId && o.date === dateStr);
    if (ov) return { enabled: ov.enabled, start: ov.start || '09:00', end: ov.end || '17:00', isOverride: true };
    const s = staff.find(st => st.id === staffId);
    const d = s?.working_hours?.[dayName];
    return { enabled: d?.enabled || false, start: d?.start || '09:00', end: d?.end || '17:00', isOverride: false };
  };

  const calcWeekHours = (staffId) => {
    let total = 0;
    DAYS.forEach((day, i) => {
      const eff = getEffective(staffId, day, weekDates[i]);
      if (eff.enabled) {
        const s = parseInt(eff.start.split(':')[0]) * 60 + parseInt(eff.start.split(':')[1]);
        const e = parseInt(eff.end.split(':')[0]) * 60 + parseInt(eff.end.split(':')[1]);
        total += (e - s) / 60;
      }
    });
    return total;
  };

  const calcDayHours = (day, dayIdx) => {
    let total = 0;
    staff.forEach(s => {
      const eff = getEffective(s.id, day, weekDates[dayIdx]);
      if (eff.enabled) {
        const st = parseInt(eff.start.split(':')[0]) * 60 + parseInt(eff.start.split(':')[1]);
        const en = parseInt(eff.end.split(':')[0]) * 60 + parseInt(eff.end.split(':')[1]);
        total += (en - st) / 60;
      }
    });
    return total;
  };

  const openCellMenu = (e, staffId, day, date) => {
    e.stopPropagation();
    setCellMenu({ staffId, day, date, x: e.clientX, y: e.clientY });
  };

  const closeCellMenu = () => setCellMenu(null);

  const openEdit = (staffId, day, date) => {
    closeCellMenu();
    const eff = getEffective(staffId, day, date);
    setEditStart(eff.enabled ? eff.start : '09:00');
    setEditEnd(eff.enabled ? eff.end : '17:00');
    setEditCell({ staffId, day, date });
  };

  const saveShift = async () => {
    if (!editCell) return;
    // Validate: end must be strictly after start (reject shifts that cross midnight — very common
    // AM/PM mistake when the iPad time picker defaults to AM)
    const toMin = (t) => { const [h, m] = (t || '0:0').split(':').map(Number); return h * 60 + m; };
    const startM = toMin(editStart);
    const endM = toMin(editEnd);
    if (endM <= startM) {
      toast.error('End time must be after start time. Did you mean PM? (e.g. 19:00 for 7pm)');
      return;
    }
    if ((endM - startM) > 16 * 60) {
      toast.error('Shift longer than 16 hours — please double-check the times');
      return;
    }
    try {
      const dateStr = format(editCell.date, 'yyyy-MM-dd');
      await axios.put(`${API}/staff/${editCell.staffId}/schedule-override`, { date: dateStr, enabled: true, start: editStart, end: editEnd }, { withCredentials: true });
      toast.success('Shift saved for ' + format(editCell.date, 'd MMM'));
      setEditCell(null);
      const r = await axios.get(`${API}/staff/schedule-overrides?date_from=${dateFrom}&date_to=${dateTo}`, { withCredentials: true });
      setOverrides(r.data || []);
    } catch { toast.error('Failed'); }
  };

  const deleteShift = async (staffId, day, date) => {
    closeCellMenu();
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      await axios.put(`${API}/staff/${staffId}/schedule-override`, { date: dateStr, enabled: false }, { withCredentials: true });
      toast.success('Day off set for ' + format(date, 'd MMM'));
      const r = await axios.get(`${API}/staff/schedule-overrides?date_from=${dateFrom}&date_to=${dateTo}`, { withCredentials: true });
      setOverrides(r.data || []);
    } catch { toast.error('Failed'); }
  };

  // Repeating shifts dialog state
  const [repeatDialog, setRepeatDialog] = useState(null); // { staffId, staffName }
  const [repeatHours, setRepeatHours] = useState({});
  const [repeatType, setRepeatType] = useState('every_week');
  const [repeatStart, setRepeatStart] = useState('');

  const openRepeatDialog = (staffId) => {
    closeCellMenu();
    const s = staff.find(st => st.id === staffId);
    if (!s) return;
    const wh = s.working_hours || {};
    const hours = {};
    DAYS.forEach(d => {
      const h = wh[d] || { enabled: false, start: '09:00', end: '17:00' };
      hours[d] = { enabled: h.enabled !== false, start: h.start || '09:00', end: h.end || '17:00' };
    });
    setRepeatHours(hours);
    setRepeatType('every_week');
    setRepeatStart(format(new Date(), 'yyyy-MM-dd'));
    setRepeatDialog({ staffId, staffName: s.name, locationIds: s.locations || [] });
  };

  const saveRepeatShifts = async () => {
    if (!repeatDialog) return;
    try {
      await axios.put(`${API}/staff/${repeatDialog.staffId}/hours`, { working_hours: repeatHours }, { withCredentials: true });
      toast.success('Repeating shifts saved');
      setRepeatDialog(null);
      onRefresh();
    } catch { toast.error('Failed to save'); }
  };

  const calcHours = (start, end) => {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    return Math.max(0, (eh * 60 + em - sh * 60 - sm) / 60);
  };
  const totalWeeklyHours = DAYS.reduce((t, d) => t + (repeatHours[d]?.enabled ? calcHours(repeatHours[d].start, repeatHours[d].end) : 0), 0);

  const TIME_OPTIONS = [];
  for (let h = 6; h <= 22; h++) {
    for (let m = 0; m < 60; m += 15) {
      const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const ampm = h >= 12 ? 'pm' : 'am';
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const label = m === 0 ? `${h12}:00 ${ampm}` : `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
      TIME_OPTIONS.push({ val, label });
    }
  }

  const setAllWorking = (staffId) => openRepeatDialog(staffId);

  const deleteAllShifts = async (staffId) => {
    closeCellMenu();
    if (!window.confirm('Delete all shifts for this team member?')) return;
    try {
      const wh = {};
      DAYS.forEach(d => { wh[d] = { enabled: false, start: '09:00', end: '17:00' }; });
      await axios.put(`${API}/staff/${staffId}/hours`, { working_hours: wh }, { withCredentials: true });
      toast.success('All shifts deleted');
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  const fmt12 = (t) => {
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'pm' : 'am';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  return (
    <div data-testid="scheduled-shifts" onClick={closeCellMenu}>
      {/* Week navigation */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('staffPage.shifts')}</h2>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} className="h-8 text-xs border-[#E5E7E1]">This week</Button>
          <Button size="sm" variant="ghost" onClick={() => setWeekStart(d => subDays(d, 7))} className="h-8 w-8 p-0"><ChevronLeft size={16} /></Button>
          <span className="text-sm font-medium text-[#1A2421] min-w-[140px] text-center">{format(weekStart, 'd')} - {format(addDays(weekStart, 6), 'd MMM yyyy')}</span>
          <Button size="sm" variant="ghost" onClick={() => setWeekStart(d => addDays(d, 7))} className="h-8 w-8 p-0"><ChevronRight size={16} /></Button>
        </div>
      </div>

      <div className="border border-[#E5E7E1] rounded-xl bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E7E1] bg-[#FAFAF8]">
                <th className="text-left px-4 py-3 text-xs font-bold tracking-wider text-[#5C6656] min-w-[160px] sticky left-0 bg-[#FAFAF8] z-10">Team member</th>
                {DAYS.map((day, i) => (
                  <th key={day} className="text-center px-2 py-2 min-w-[110px]">
                    <p className="text-xs font-bold text-[#1A2421]">{format(weekDates[i], 'EEE d MMM')}</p>
                    <p className="text-[10px] text-[#5C6656]">{calcDayHours(day, i).toFixed(0)} hrs</p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {staff.map(s => {
                const weekHrs = calcWeekHours(s.id);
                return (
                  <tr key={s.id} className="border-b border-[#E5E7E1]/50" data-testid={`shift-row-${s.id}`}>
                    <td className="px-4 py-3 sticky left-0 bg-white z-10">
                      <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full bg-[#E9EDDF] flex items-center justify-center text-xs font-bold text-[#556B2F]">{s.name?.charAt(0)?.toUpperCase()}</div>
                        <div>
                          <p className="font-semibold text-[#1A2421] text-sm uppercase">{s.name}</p>
                          <p className="text-[10px] text-[#5C6656]">{weekHrs > 0 ? `${weekHrs.toFixed(0)} hrs` : 'No shifts'}</p>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); setCellMenu({ staffId: s.id, day: null, x: e.clientX, y: e.clientY }); }} className="ml-auto p-1 text-[#5C6656] hover:text-[#556B2F]" data-testid={`staff-menu-${s.id}`}>
                          <Pencil size={13} />
                        </button>
                      </div>
                    </td>
                    {DAYS.map((day, i) => {
                      const eff = getEffective(s.id, day, weekDates[i]);
                      return (
                        <td key={day} className="px-1 py-1 text-center">
                          <button onClick={(e) => openCellMenu(e, s.id, day, weekDates[i])}
                            className={`w-full py-3 px-2 rounded-lg text-xs transition-colors ${eff.enabled ? 'bg-[#F0F2EB] hover:bg-[#E9EDDF] cursor-pointer' : 'hover:bg-[#F5F7F0] cursor-pointer text-[#B0B5A8]'}`}
                            data-testid={`shift-cell-${s.id}-${day}`}>
                            {eff.enabled ? (
                              <div>
                                <span className="font-medium text-[#1A2421]">{fmt12(eff.start)} - {fmt12(eff.end)}</span>
                                {eff.isOverride && <span className="block text-[8px] text-[#556B2F] font-bold mt-0.5">CUSTOM</span>}
                              </div>
                            ) : (
                              <span>Not working</span>
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {staff.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#5C6656]">No staff members</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Context menu for shift cell */}
      {cellMenu && (
        <div className="fixed bg-white border border-[#E5E7E1] rounded-xl shadow-xl z-50 py-2 w-52" style={{ left: Math.min(cellMenu.x, window.innerWidth - 220), top: cellMenu.y }} data-testid="shift-context-menu">
          {cellMenu.day ? (() => {
            const eff = getEffective(cellMenu.staffId, cellMenu.day, cellMenu.date);
            return eff.enabled ? (
              <>
                <button onClick={() => openEdit(cellMenu.staffId, cellMenu.day, cellMenu.date)} className="w-full text-left px-4 py-2 text-sm hover:bg-[#F5F7F0] text-[#1A2421]">Edit this day</button>
                <button onClick={() => setAllWorking(cellMenu.staffId)} className="w-full text-left px-4 py-2 text-sm hover:bg-[#F5F7F0] text-[#1A2421]">Set repeating shifts</button>
                <button onClick={() => deleteShift(cellMenu.staffId, cellMenu.day, cellMenu.date)} className="w-full text-left px-4 py-2 text-sm hover:bg-red-50 text-[#E2725B]">Remove this day</button>
                {eff.isOverride && <button onClick={async () => {
                  closeCellMenu();
                  try {
                    const dateStr = format(cellMenu.date, 'yyyy-MM-dd');
                    await axios.delete(`${API}/staff/${cellMenu.staffId}/schedule-override?date=${dateStr}`, { withCredentials: true });
                    toast.success('Reset to weekly pattern');
                    const r = await axios.get(`${API}/staff/schedule-overrides?date_from=${dateFrom}&date_to=${dateTo}`, { withCredentials: true });
                    setOverrides(r.data || []);
                  } catch { toast.error('Failed'); }
                }} className="w-full text-left px-4 py-2 text-sm hover:bg-[#F5F7F0] text-[#5C6656]">Reset to default pattern</button>}
              </>
            ) : (
              <>
                <button onClick={() => openEdit(cellMenu.staffId, cellMenu.day, cellMenu.date)} className="w-full text-left px-4 py-2 text-sm hover:bg-[#F5F7F0] text-[#1A2421]">Add shift</button>
                <button onClick={() => setAllWorking(cellMenu.staffId)} className="w-full text-left px-4 py-2 text-sm hover:bg-[#F5F7F0] text-[#1A2421]">Set repeating shifts</button>
              </>
            );
          })() : (
            <>
              <p className="px-4 py-1 text-xs font-bold text-[#5C6656] uppercase tracking-wider">Schedule</p>
              <button onClick={() => setAllWorking(cellMenu.staffId)} className="w-full text-left px-4 py-2 text-sm hover:bg-[#F5F7F0] text-[#1A2421]">Set repeating shifts</button>
              <button onClick={() => deleteAllShifts(cellMenu.staffId)} className="w-full text-left px-4 py-2 text-sm hover:bg-red-50 text-[#E2725B]">Delete all shifts</button>
              <div className="border-t border-[#E5E7E1] my-1" />
              <p className="px-4 py-1 text-xs font-bold text-[#5C6656] uppercase tracking-wider">Team member</p>
              <button onClick={() => { closeCellMenu(); }} className="w-full text-left px-4 py-2 text-sm hover:bg-[#F5F7F0] text-[#1A2421]">View team member</button>
            </>
          )}
        </div>
      )}

      {/* Edit shift dialog */}
      <Dialog open={!!editCell} onOpenChange={() => setEditCell(null)}>
        <DialogContent className="sm:max-w-xs border-[#E5E7E1]">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>Edit shift — {editCell ? DAY_ABBR[editCell.day] : ''}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Start</Label><Input type="time" value={editStart} onChange={e => setEditStart(e.target.value)} className="border-[#E5E7E1]" data-testid="shift-edit-start" /></div>
              <div className="space-y-1"><Label className="text-xs">End</Label><Input type="time" value={editEnd} onChange={e => setEditEnd(e.target.value)} className="border-[#E5E7E1]" data-testid="shift-edit-end" /></div>
            </div>
            <div className="flex gap-2">
              <Button onClick={saveShift} className="flex-1 bg-[#556B2F] hover:bg-[#4A5D23] text-white" data-testid="save-shift-btn">Save</Button>
              <Button variant="outline" onClick={() => setEditCell(null)} className="border-[#E5E7E1]">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Repeating Shifts Full-Page Dialog */}
      {repeatDialog && (
        <div className="fixed inset-0 z-50 bg-white overflow-y-auto" data-testid="repeat-shifts-dialog">
          <div className="max-w-5xl mx-auto px-6 py-8">
            <div className="flex items-center justify-between mb-8">
              <div />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setRepeatDialog(null)} className="border-[#E5E7E1]">Close</Button>
                <Button onClick={saveRepeatShifts} className="bg-[#1A2421] hover:bg-[#2D3B36] text-white" data-testid="save-repeat-btn">Save</Button>
              </div>
            </div>
            <h1 className="text-3xl font-bold text-[#1A2421] mb-2" style={{ fontFamily: 'Outfit' }}>Set {repeatDialog.staffName}'s repeating shifts</h1>
            <p className="text-sm text-[#5C6656] mb-8">Set weekly, fortnightly or custom shifts. Saved changes will apply to all upcoming shifts for the selected period.</p>
            <div className="flex gap-12">
              {/* Left sidebar */}
              <div className="w-72 flex-shrink-0 space-y-6">
                <div className="border border-[#E5E7E1] rounded-xl p-5 space-y-1">
                  {(() => {
                    const staffLocs = (repeatDialog.locationIds || []).length > 0
                      ? locations.filter(l => repeatDialog.locationIds.includes(l.id))
                      : locations;
                    if (staffLocs.length > 0) {
                      return staffLocs.map(loc => (
                        <div key={loc.id}>
                          <p className="font-bold text-[#1A2421]">{loc.name}</p>
                          <p className="text-xs text-[#5C6656]">{[loc.address, loc.city, loc.country].filter(Boolean).join(', ')}</p>
                        </div>
                      ));
                    }
                    return <p className="text-xs text-[#5C6656]">All locations</p>;
                  })()}
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-bold">Schedule type</Label>
                    <Select value={repeatType} onValueChange={setRepeatType}>
                      <SelectTrigger className="border-[#E5E7E1]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="every_week">Every week</SelectItem>
                        <SelectItem value="every_2_weeks">Every 2 weeks</SelectItem>
                        <SelectItem value="every_3_weeks">Every 3 weeks</SelectItem>
                        <SelectItem value="every_4_weeks">Every 4 weeks</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-bold">Start date</Label>
                    <Input type="date" value={repeatStart} onChange={e => setRepeatStart(e.target.value)} className="border-[#E5E7E1]" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-bold">Ends</Label>
                    <Select value="never" onValueChange={() => {}}>
                      <SelectTrigger className="border-[#E5E7E1]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="never">Never</SelectItem>
                        <SelectItem value="after_date">After specific date</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="bg-[#F5F7F0] rounded-lg p-4 flex items-start gap-2">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#556B2F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 flex-shrink-0"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  <p className="text-xs text-[#5C6656]">Team members will not be scheduled on business closed periods.</p>
                </div>
              </div>
              {/* Right: Weekly hours grid */}
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-[#1A2421] mb-1" style={{ fontFamily: 'Outfit' }}>Weekly</h2>
                <p className="text-sm text-[#5C6656] mb-6">{totalWeeklyHours.toFixed(0)} hours total</p>
                <div className="space-y-3">
                  {DAYS.map(day => {
                    const h = repeatHours[day] || { enabled: false, start: '09:00', end: '17:00' };
                    const hrs = h.enabled ? calcHours(h.start, h.end) : 0;
                    const dayLabel = day.charAt(0).toUpperCase() + day.slice(1);
                    return (
                      <div key={day} className="flex items-center gap-4" data-testid={`repeat-day-${day}`}>
                        <div className="flex items-center gap-3 w-32">
                          <div className={`w-6 h-6 rounded border-2 flex items-center justify-center cursor-pointer ${h.enabled ? 'bg-[#556B2F] border-[#556B2F]' : 'border-[#D1D5CB]'}`}
                            onClick={() => setRepeatHours(prev => ({ ...prev, [day]: { ...prev[day], enabled: !h.enabled } }))}>
                            {h.enabled && <svg width="14" height="14" viewBox="0 0 12 12" fill="none"><path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-[#1A2421]">{dayLabel}</p>
                            {h.enabled && <p className="text-[10px] text-[#5C6656]">{hrs.toFixed(0)} hrs</p>}
                          </div>
                        </div>
                        {h.enabled ? (
                          <div className="flex items-center gap-2">
                            <select value={h.start} onChange={e => setRepeatHours(prev => ({ ...prev, [day]: { ...prev[day], start: e.target.value } }))}
                              className="h-10 px-3 border border-[#E5E7E1] rounded-lg text-sm bg-white">
                              {TIME_OPTIONS.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
                            </select>
                            <span className="text-sm text-[#5C6656]">to</span>
                            <select value={h.end} onChange={e => setRepeatHours(prev => ({ ...prev, [day]: { ...prev[day], end: e.target.value } }))}
                              className="h-10 px-3 border border-[#E5E7E1] rounded-lg text-sm bg-white">
                              {TIME_OPTIONS.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
                            </select>
                            <button onClick={() => setRepeatHours(prev => ({ ...prev, [day]: { ...prev[day], enabled: false } }))}
                              className="ml-2 text-[#E2725B] hover:text-[#C95A44]"><Trash2 size={16} /></button>
                          </div>
                        ) : (
                          <p className="text-sm text-[#5C6656] italic">Day off</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
